import { ColumnDef, DeepKeys } from "@tanstack/react-table";

import { IWorkspace } from "./workspace.interface";

import { TPermissionData } from "@/pages/workforce/admin/components/AddAdminPermission";

export enum EmploymentType {
  "Full Time" = "FULLTIME",
  "Part Time" = "PARTTIME",
  "Casual" = "CASUAL",
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type TAny = any;

export type TGeo = {
  latitude?: string;
  longitude?: string;
  label?: string;
};

export enum EmployeeStatus {
  ACTIVE = "ACTIVE",
  DEACTIVATED = "DEACTIVATED",
  TERMINATED = "TERMINATED",
  ACTIVATE = "ACTIVATE",
  DEACTIVATE = "DEACTIVATE",
}

export type TStatus =
  | EmployeeStatus.ACTIVE
  | EmployeeStatus.DEACTIVATED
  | EmployeeStatus.TERMINATED;

export type Roles = "SUPER_ADMIN" | "ADMIN" | "MANAGER" | "STAFF" | "EMPLOYEE";

export type TRole =
  | "SUPERADMIN"
  | "ADMIN"
  | "MANAGER"
  | "EMPLOYEE"
  | "CUSTOMER"
  | "CUSTOMERUSER"
  | "ACCOUNT";

export enum RoleEnum {
  SUPER_ADMIN = "SUPER_ADMIN",
  ADMIN = "ADMIN",
  MANAGER = "MANAGER",
  EMPLOYEE = "EMPLOYEE",
  CUSTOMERUSER = "CUSTOMERUSER",
  ACCOUNT = "ACCOUNT",
}

export interface IWorkForceUserEntity {
  id: string;
  createdAt: string;
  updatedAt: string;
  email?: string;
  phoneNumber?: string;
  status?: TStatus;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  address?: string;
  image?: string;
  otpHash?: string;
  deviceId?: string;
  permissions?: TPermissionData;
  role?: TRole;
  employmentType?: EmploymentType;
  tabAccess: boolean;
}

export interface IUserAccessList {
  id: string;
  userId?: string;
  workspace?: IWorkspace[];
}

export enum Permissions {
  ALL = "ALL",
  DASHBOARD = "DASHBOARD",
  ROSTER = "ROSTER",
  PROJECT_REGISTER = "PROJECT_REGISTER",
  ESTIMATES = "ESTIMATES",
  CLAIMS = "CLAIMS",
  PROJECTS = "PROJECTS",
  PROJECTS_PROJECT = "PROJECTS_PROJECT",
  WORKFORCE = "WORKFORCE",
  WORKFORCE_ADMIN = "WORKFORCE_ADMIN",
  WORKFORCE_MANAGER = "WORKFORCE_MANAGER",
  WORKFORCE_EMPLOYEE = "WORKFORCE_EMPLOYEE",
  WORKFORCE_SUBCONTRACTOR = "WORKFORCE_SUBCONTRACTOR",
  WORKFORCE_SETTINGS = "WORKFORCE_SETTINGS",
  CUSTOMER = "CUSTOMER",
  CUSTOMER_CUSTOMER = "CUSTOMER_CUSTOMER",
  CUSTOMER_PROSPECT = "CUSTOMER_PROSPECT",
  WASTEMANAGE = "WASTEMANAGE",
  WASTEMANAGE_SUMMARY = "WASTEMANAGE_SUMMARY",
  WASTEMANAGE_WEIGHTSCALE = "WASTEMANAGE_WEIGHTSCALE",
  WASTEMANAGE_WEIGHTINGREPORTS = "WASTEMANAGE_WEIGHTINGREPORTS",
  WASTEMANAGE_WEIGHBRIDGE = "WASTEMANAGE_WEIGHBRIDGE",
  WASTEMANAGE_VEHICLES = "WASTEMANAGE_VEHICLES",
  WASTEMANAGE_LOADTYPE = "WASTEMANAGE_LOADTYPE",
  WASTEMANAGE_WASTETYPE = "WASTEMANAGE_WASTETYPE",
  WASTEMANAGE_DAILYREPORTS = "WASTEMANAGE_DAILYREPORTS",
  WASTEMANAGE_SETTINGS = "WASTEMANAGE_SETTINGS",
  REPORTS = "REPORTS",
  FLEETS = "FLEETS",
  REPORTS_DAILYREPORT = "REPORTS_DAILYREPORT",
  REPORTS_WORKDOCKET = "REPORTS_WORKDOCKET",
  REPORTS_WORKDOCKET_EMPLOYEE = "REPORTS_WORKDOCKET_EMPLOYEE",
  INVOICE = "INVOICE",
  INVOICE_DOCKET = "INVOICE_DOCKET",
  INVOICE_CONTRACT = "INVOICE_CONTRACT",
  WAREHOUSE = "WAREHOUSE",
  TIMETRACKING = "TIMETRACKING",
  TIMETRACKING_SUMMARY = "TIMETRACKING_SUMMARY",
  TIMETRACKING_TIMESHEET = "TIMETRACKING_TIMESHEET",
  TIMETRACKING_SUBCONTRACTOR = "TIMETRACKING_SUBCONTRACTOR",
  TIMETRACKING_WORKLOGS = "TIMETRACKING_WORKLOGS",
  TIMETRACKING_SETTINGS = "TIMETRACKING_SETTINGS",
  PAYROLL = "PAYROLL",
  PAYROLL_CASE2 = "PAYROLL_CASE2",
  PAYROLL_PART1 = "PAYROLL_PART1",
  ITEMS = "ITEMS",
  CALENDAR = "CALENDAR",
  TOOLS = "TOOLS",
  MESSAGES = "MESSAGES",
  TEMPLATE = "TEMPLATE",
  TASKS = "TASKS",
  OHS = "OHS",
  SUPPORT = "SUPPORT",
  ANNOUNCEMENT = "ANNOUNCEMENT",
  SETTING = "SETTING",
  PROJECTDIRECTORY = "PROJECTDIRECTORY",
  INTELLIFLOW = "INTELLIFLOW",
  ACTIVITY = "ACTIVITY",

  // customer portal
  CUSTOMER_PORTAL = "CUSTOMER_PORTAL",
  CP_DASHBOARD = "CP_DASHBOARD",
  CP_USERMANAGEMENT = "CP_USERMANAGEMENT",
  CP_PROJECTS = "CP_PROJECTS",
  CP_INVOICES = "CP_INVOICES",
  CP_WORKDOCKETS = "CP_WORKDOCKETS",
  CP_WASTEMANAGEMENT = "CP_WASTEMANAGEMENT",
  CP_WAREHOUSING = "CP_WAREHOUSING",
  CP_SUPPORT = "CP_SUPPORT",
}

export enum Action {
  READ = "READ",
  WRITE = "WRITE",
}

declare module "@tanstack/react-table" {
  /*
    * The additional properties that can be passed to the ColumnMeta
    {minWidth} - The minimum width of the column
    {className} - The class name of the column
    {classNameRow} - The class name of the row
    {showRightBorder} - Show right border of the column
    {data} - The data of the column
    {column} - The column definition
    {headerStyle} - The style of the header
    {minHeight} - The minimum height of the column
  */
  interface ColumnMeta<TData, TValue> {
    minWidth?: number;
    className?: string;
    classNameRow?: string;
    showRightBorder?: boolean;
    data?: TData;
    column?: ColumnDef<TData, TValue>;
    headerStyle?: React.CSSProperties;
    showCustomBorder?: boolean;
  }
}

export type ExtendedColumnDef<T, V = unknown> = ColumnDef<T, V> & {
  idForRowClick?: keyof T;
  accessorKey?: DeepKeys<T> | string;
  map?: {
    key: string;
    valueFrom?: DeepKeys<T> | string;
    mergedValuesFrom?: (DeepKeys<T> | string)[];
    dataTransformer?: (data: T) => string;
    excludeFromExport?: boolean;
  };
  hideColumn?: boolean;
  sortBy?: string;
};

export enum EmployeeOptionsType {
  SKILLS = "SKILLS",
  TRAININGS = "TRAININGS",
}

export type _TFloatingMenuCustomComponentProps = Partial<{
  ids: string[];
  close: () => void;
}>;

export type SocketNamespace = "" | "/";

export enum SOCKET_EVENT {
  CREATE = "create",
  UPDATE = "update",
  DELETE = "delete",
}

export type SocketData = {
  action: SOCKET_EVENT;
  userId: string;
};
